// ISO/IEC 27001:2022 controls-mapping admin API client (item 64 of
// PRODUCT-ROADMAP.md, Phase B).
//
// Backs the AdminComplianceISO27001 page. Two endpoints — one for the
// static 93-control catalogue (cached on the client without
// revalidation), one for the live-probe snapshot that refreshes every
// 30 s while the page is open.

import useSWR from 'swr';

export type ISO27001Status = 'implemented' | 'configurable' | 'customer_side';

export type ISO27001Section = 'A.5' | 'A.6' | 'A.7' | 'A.8';

export interface ISO27001Control {
  id: string;
  section: ISO27001Section;
  title: string;
  status: ISO27001Status;
  feature?: string;
  evidence?: string;
  snapshot_key?: string;
}

export type ProbeTone = 'ok' | 'warn' | 'fail' | 'info';

export interface ISO27001Probe {
  tone: ProbeTone;
  headline: string;
  details?: Record<string, unknown>;
}

export interface ISO27001Catalogue {
  counts: Record<ISO27001Status, number>;
  controls: ISO27001Control[];
}

export interface ISO27001Snapshot extends ISO27001Catalogue {
  generated_at: string;
  states: Record<string, ISO27001Probe>;
}

const json = (url: string) =>
  fetch(url, { credentials: 'include' }).then((r) => {
    if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
    return r.json();
  });

export function useISO27001Controls() {
  return useSWR<ISO27001Catalogue>(
    '/api/admin/compliance/iso27001/controls',
    json,
    { revalidateOnFocus: false },
  );
}

export function useISO27001Snapshot() {
  return useSWR<ISO27001Snapshot>(
    '/api/admin/compliance/iso27001/snapshot',
    json,
    { refreshInterval: 30_000 },
  );
}

// Section titles match docs-site/compliance/iso27001-controls.md
// headings.
export const SECTION_TITLES: Record<ISO27001Section, string> = {
  'A.5': 'Organizational controls',
  'A.6': 'People controls',
  'A.7': 'Physical controls',
  'A.8': 'Technological controls',
};

export const STATUS_LABELS: Record<ISO27001Status, string> = {
  implemented: 'Implemented',
  configurable: 'Configurable',
  customer_side: 'Customer-side',
};

// Build a Statement-of-Applicability CSV. Bound to the catalogue
// shape so a downstream auditor's spreadsheet ingest is one paste.
export function buildSoACsv(controls: ISO27001Control[]): string {
  const escape = (s: string) => {
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replaceAll('"', '""')}"`;
    }
    return s;
  };
  const header = ['Annex A ref', 'Title', 'Applicable?', 'OrbitalReg status', 'Feature', 'Evidence']
    .map(escape)
    .join(',');
  const lines = controls.map((c) =>
    [
      c.id,
      c.title,
      c.status === 'customer_side' ? 'Customer-side' : 'Yes',
      STATUS_LABELS[c.status],
      c.feature ?? '',
      c.evidence ?? '',
    ]
      .map((s) => escape(s ?? ''))
      .join(','),
  );
  return [header, ...lines].join('\n') + '\n';
}
